import distance from '@turf/distance';
import destination from '@turf/destination';

import { Units } from '@turf/helpers';
import { ILabelLayer } from '../../../typings/map';

/**
 * @param {GeoJSON.BBox} bbox
 * @param {number} gridWidth
 * @param {number} gridHeight
 * @param {Units} units
 * @returns {GeoJSON.Feature<GeoJSON.LineString>[]}
 */
export function getGrid(
  bbox: GeoJSON.BBox,
  gridWidth: number,
  gridHeight: number,
  units: Units
) {
  const earthCircumference = Math.ceil(
    distance([0, 0], [180, 0], { units }) * 2
  );
  const maxColumns = Math.floor(earthCircumference / gridWidth);

  const fullDistance = (
    from: GeoJSON.Position,
    to: GeoJSON.Position,
    options: { units: Units }
  ) => {
    const dist = distance(from, to, options);
    if (Math.abs(to[0] - from[0]) >= 180) {
      return earthCircumference - dist;
    }
    return dist;
  };

  /** @type {GeoJSON.Feature<GeoJSON.LineString>[]} */
  const features = [];
  const labels = [];
  const west = bbox[0];
  const south = bbox[1];
  const east = bbox[2];
  const north = bbox[3];

  // calculate grid start point
  const deltaX =
    (west < 0 ? -1 : 1) * fullDistance([0, 0], [west, 0], { units });
  const deltaY =
    (south < 0 ? -1 : 1) * fullDistance([0, 0], [0, south], { units });
  const startDeltaX = Math.ceil(deltaX / gridWidth) * gridWidth;
  const startDeltaY = Math.ceil(deltaY / gridHeight) * gridHeight;

  const startPoint: GeoJSON.Position = [
    destination([0, 0], startDeltaX, 90, { units }).geometry.coordinates[0],
    destination([0, 0], startDeltaY, 0, { units }).geometry.coordinates[1],
  ];

  // calculate grid columns and rows count
  const width = fullDistance([west, 0], [east, 0], { units });
  const height = fullDistance([0, south], [0, north], { units });
  const columns = Math.min(Math.ceil(width / gridWidth), maxColumns);
  const rows = Math.ceil(height / gridHeight);

  let currentPoint: GeoJSON.Position;

  // meridians
  currentPoint = startPoint;
  for (let i = 0; i < columns; i++) {
    const coordinates: GeoJSON.Position[] = [
      [currentPoint[0], south],
      [currentPoint[0], north],
    ];

    const feature: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties: {},
    };
    features.push(feature);

    const southernPoint: GeoJSON.Position = [currentPoint[0], south];
    const longitude = Math.round(currentPoint[0]);
    // if we work with 'E' and 'W' for longitude unit then the 'minus' should be removed in front of the number value
    // when in western hemisphere- otherwise it is 'double effect'  (alternatively just fix the units to 'E' and 'N').
    const lngLabel = longitude !== 0 ? longitude + 'E' : '0';
    const southernFeature: ILabelLayer = {
      coordinates: southernPoint,
      name: lngLabel,
      color: [119, 136, 153],
    };
    labels.push(southernFeature);
    const northernPoint: GeoJSON.Position = [currentPoint[0], north];
    const northernFeature: ILabelLayer = {
      coordinates: northernPoint,
      name: lngLabel,
      color: [119, 136, 153],
    };
    labels.push(northernFeature);

    currentPoint = [
      destination([currentPoint[0], 0], gridWidth, 90, { units }).geometry
        .coordinates[0],
      currentPoint[1],
    ];
  }

  // parallels
  currentPoint = startPoint;
  for (let i = 0; i < rows; i++) {
    const coordinates: GeoJSON.Position[] = [
      [west, currentPoint[1]],
      [east, currentPoint[1]],
    ];
    const feature: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties: {},
    };
    features.push(feature);

    const westernPoint: GeoJSON.Position = [west, currentPoint[1]];
    const latitude = Math.round(currentPoint[1]);
    const latLabel = latitude !== 0 ? latitude + 'N' : '0';
    const westernFeature: ILabelLayer = {
      coordinates: westernPoint,
      name: latLabel,
      color: [119, 136, 153],
    };
    labels.push(westernFeature);
    const easternPoint: GeoJSON.Position = [east, currentPoint[1]];
    const easternFeature: ILabelLayer = {
      coordinates: easternPoint,
      name: latLabel,
      color: [119, 136, 153],
      textAnchor: 'end',
    };
    labels.push(easternFeature);

    currentPoint = [
      currentPoint[0],
      destination([0, currentPoint[1]], gridHeight, 0, { units }).geometry
        .coordinates[1],
    ];
  }

  return { lines: features, points: labels };
}
